import React, { useEffect, useState } from "react";
import { Loader2, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { FIELD, inr } from "./_shared";

/** Production Hours — REGULAR hours are auto-computed from station start→done
 *  timestamps. Users only log OVERTIME (1 OT hour = 1.5×). */
export default function ProductionHoursCard({ p, user }) {
  const role = user?.role;
  const canView = ["super_admin", "accounts", "admin",
                    "production_coordination", "production"].includes(role);
  const canWrite = ["super_admin", "production"].includes(role);
  const showCost = ["super_admin", "accounts", "admin"].includes(role);

  const [state, setState] = useState(null);
  const [stations, setStations] = useState([]);
  const [form, setForm] = useState({
    station_key: "",
    work_date: new Date().toISOString().slice(0, 10),
    ot_hours: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/costing/project/${p.id}/production-hours`); setState(data); }
    catch { /* noop */ }
  };
  const loadStations = async () => {
    try { const { data } = await api.get(`/project/stations`); setStations(data.stations || []); }
    catch { /* noop */ }
  };
  useEffect(() => {
    if (canView) { load(); loadStations(); }
    /* eslint-disable-next-line */
  }, []);
  if (!canView) return null;
  if (!state) return <div className="hk-card p-4" data-testid="production-hours-card">
    <div className="hk-tiny-label mb-2">Production hours</div>
    <div className="text-xs text-slate-400">Loading…</div>
  </div>;

  const submit = async () => {
    if (!form.station_key || !form.work_date) { toast.error("Pick a station + date"); return; }
    const ot = Number(form.ot_hours || 0);
    if (ot <= 0) { toast.error("OT hours must be greater than zero"); return; }
    setSaving(true);
    try {
      await api.post(`/costing/project/${p.id}/production-hours`, {
        station_key: form.station_key, work_date: form.work_date,
        regular_hours: 0, ot_hours: ot, notes: form.notes || null,
      });
      toast.success("OT logged");
      setForm({ ...form, ot_hours: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this OT entry?")) return;
    try { await api.delete(`/costing/project/${p.id}/production-hours/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const preview = (() => {
    const ot = Number(form.ot_hours || 0);
    if (ot <= 0) return null;
    const eff = ot * 1.5;
    const rate = Number(state.rates?.[form.station_key] || 0);
    return { eff, cost: eff * rate, hasRate: !!rate };
  })();
  const stationOptions = stations.filter(
    (s) => s.owner_role !== "installation" || role === "super_admin"
  );

  return (
    <div className="hk-card p-4" data-testid="production-hours-card">
      <div className="hk-tiny-label mb-2">Production hours</div>

      {/* Auto-computed regular hours (from station timestamps) */}
      <div className="border border-slate-200 bg-slate-50 rounded-sm p-2 mb-3 space-y-1" data-testid="ph-auto-regular">
        <div className="flex items-center gap-1 text-[10px] font-bold uppercase text-slate-600">
          <Clock className="w-3 h-3" /> Regular hours (auto, from station start → done)
        </div>
        {state.auto_regular?.per_station?.length ? (
          <ul className="space-y-0.5 text-[11px]">
            {state.auto_regular.per_station.map((s) => (
              <li key={s.station_key} className="flex justify-between">
                <span className="text-slate-700">
                  {s.station_name}
                  {s.in_progress && <span className="ml-1 text-amber-600">· running</span>}
                </span>
                <span className="font-mono text-slate-900">
                  <b>{s.regular_hours}h</b>
                  {showCost && <> · {inr(s.cost)}</>}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-[11px] text-slate-500">
            Regular hours will accrue automatically once production starts.
          </div>
        )}
        <div className="pt-1 border-t border-slate-200 flex justify-between text-xs">
          <span className="font-bold text-slate-700">
            Total regular: {state.auto_regular?.total_hours || 0}h
          </span>
          {showCost && <span className="font-bold text-slate-900">
            {inr(state.regular_cost || 0)}
          </span>}
        </div>
      </div>

      {/* OT entry — the only manual input */}
      {canWrite && (
        <div className="border border-slate-200 rounded-sm p-2 mb-3 space-y-1">
          <div className="text-[10px] font-bold uppercase text-slate-600">
            Log overtime (OT)
          </div>
          <select className={FIELD} value={form.station_key}
            onChange={(e) => setForm({ ...form, station_key: e.target.value })}
            data-testid="ph-station">
            <option value="">Station…</option>
            {stationOptions.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-1">
            <input className={FIELD} type="date" value={form.work_date}
              onChange={(e) => setForm({ ...form, work_date: e.target.value })}
              data-testid="ph-date" />
            <input className={FIELD} type="number" step="0.25" min="0" placeholder="OT hrs *"
              value={form.ot_hours}
              onChange={(e) => setForm({ ...form, ot_hours: e.target.value })}
              required
              data-testid="ph-ot" />
          </div>
          <input className={FIELD} placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="ph-notes" />
          {preview && (
            <div className="text-[10px] text-slate-500" data-testid="ph-preview">
              Effective OT ≈ <b>{preview.eff.toFixed(2)} hrs</b> (1 OT = 1.5×)
              {showCost && (preview.hasRate
                ? <> · Cost ≈ <b>{inr(preview.cost)}</b></>
                : <> · rate not set</>)}
            </div>
          )}
          <button type="button" onClick={submit}
            disabled={saving || !form.station_key || !form.ot_hours}
            data-testid="ph-submit"
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-sm text-xs">
            {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Log OT
          </button>
        </div>
      )}

      {/* Manual OT rows */}
      {state.ot_rows.length === 0 ? (
        <div className="text-xs text-slate-400" data-testid="ph-empty">No OT logged yet.</div>
      ) : (
        <>
          <div className="text-[10px] font-bold uppercase text-slate-600 mb-1">OT log</div>
          <ul className="space-y-1.5" data-testid="ph-list">
            {state.ot_rows.map((r) => {
              const canDelete = role === "super_admin"
                || (role === "production" && r.user_id === user?.id);
              return (
                <li key={r.id} className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900">{r.station_name}</div>
                      <div className="text-slate-600">
                        {r.work_date} · OT {r.ot_hours}h = <b>{r.effective_hours}h</b> effective
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        By {r.user_name}
                        {showCost && <> · Cost: <b>{inr(r.cost)}</b> @ {inr(r.rate_per_hour)}/hr</>}
                      </div>
                      {r.notes && <div className="text-slate-500 mt-0.5">{r.notes}</div>}
                    </div>
                    {canDelete && (
                      <button type="button" onClick={() => del(r.id)}
                        data-testid={`ph-del-${r.id}`}
                        className="text-red-600 hover:text-red-800 shrink-0" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* Totals */}
      <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between text-xs"
        data-testid="ph-totals">
        <span className="font-bold text-slate-700">
          Total effective: {state.total_hours}h
          <span className="text-[10px] text-slate-400 ml-1">
            (regular {state.auto_regular?.total_hours || 0} + OT×1.5 {state.ot_effective_hours || 0})
          </span>
        </span>
        {showCost && <span className="font-bold text-slate-900">{inr(state.total_cost)}</span>}
      </div>
      {(state.by_station || []).length > 1 && showCost && (
        <div className="mt-2 pt-2 border-t border-slate-200 space-y-0.5 text-[10px]"
          data-testid="ph-by-station">
          <div className="hk-tiny-label mb-0.5">By station</div>
          {state.by_station.map((s) => (
            <div key={s.station_key} className="flex justify-between text-slate-600">
              <span>{s.station_name}</span>
              <span>{s.effective_hours}h · {inr(s.cost)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
